import React, { useRef } from 'react';
import { H2, Form, useForm, Errors } from '/src/components';
import { cloudflareGetRecaptchaToken, cloudflareUseRecaptcha } from '/src/services';
import * as styles from './style.scss';

const OTP_LENGTH = 6;

const OneTimePassword = ({
    translate,
    cloudflareSiteKey,
    requestPasswordOtp,
    loginOtp,
    hideModal = f => f,
    isLoading,
    handleBack,
    idTurnstile = 'cf-turnstile-reset-one-time-password',
    email,
}) => {
       const {
        data,
        errors,
        setErrors,
        handleChange,
        validateData,
        setFormValues,
        fieldsErrors,
    } = useForm({ '0': '', '1': '', '2': '', '3': '', '4': '', '5': '' }, {
        '0': { isRequired: true },
        '1': { isRequired: true },
        '2': { isRequired: true },
        '3': { isRequired: true },
        '4': { isRequired: true },
        '5': { isRequired: true },
    });
    const inputRefs = useRef([]);
    const { cloudflareResetRecaptcha } = cloudflareUseRecaptcha(cloudflareSiteKey, `#${idTurnstile}`);

    const getCode = () => Object.values(data).join("");

    const handleRequestPasswordOtp = e => {
        e.preventDefault();

        const recaptchaToken = cloudflareGetRecaptchaToken();

        if (!recaptchaToken) {
            setErrors([{ message: 'Check recaptcha' }]);
        } else {
            requestPasswordOtp({ email }, { 'cf-turnstile-response': recaptchaToken }).then(() => {
                cloudflareResetRecaptcha();
            }).catch(errors => {
                cloudflareResetRecaptcha();
                setErrors(errors);
            });
        }
    };

    const handleLogin = e => {
        e.preventDefault();
        const isValidData = validateData();

        const recaptchaToken = cloudflareGetRecaptchaToken();

        if (!recaptchaToken) {
            setErrors([{ message: 'Check recaptcha' }]);
        } else if (isValidData) {
            loginOtp({ code: getCode(), email }, { 'cf-turnstile-response': recaptchaToken }).then(() => {
                hideModal();
            }).catch(errors => {
                cloudflareResetRecaptcha();
                setErrors(errors);
            });
        }
    };

    const onFocus = e => e.target.select();

    const setCode = (numbers) => {
        if (numbers) {
            setFormValues({
                data: {
                    '0': numbers[0] || '',
                    '1': numbers[1] || '',
                    '2': numbers[2] || '',
                    '3': numbers[3] || '',
                    '4': numbers[4] || '',
                    '5': numbers[5] || ''
                }
            });

            inputRefs.current[Math.min(numbers.length, OTP_LENGTH - 1)]?.focus();
        }
    };

    const onChange = (e, index) => {
        const digits = e.target.value.replace(/[^0-9]/g, '');

        if (digits.length > 1) {
            const numbers = digits.slice(0, OTP_LENGTH);

            setCode(numbers);

            return;
        }

        const digit = digits.slice(-1);
        handleChange({ target: { name: e.target.name, value: digit } });

        if (digit && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const onKeyDown = (e, index) => {
        if (
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Tab' ||
            e.ctrlKey ||
            e.metaKey
        ) {
            if (e.key === 'Backspace' && !data[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }

            if (e.key === 'ArrowLeft' && index > 0) {
                e.preventDefault();
                inputRefs.current[index - 1]?.focus();
            }

            if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
                e.preventDefault();
                inputRefs.current[index + 1]?.focus();
            }

            return;
        }

        if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const onPaste = e => {
        e.preventDefault();

        const numbers = String(e.clipboardData.getData('text')).replace(/[^0-9]/g, '').slice(0, OTP_LENGTH);

        setCode(numbers);
    };

    return (
        <>
            <H2 className={styles.title}>
                {translate('Enter one time password (otp)')}
            </H2>
            <div className={styles.send}>
                {translate('We sent a 6-digit code to')}
            </div>
            <div className={styles.email}>
                {email}
            </div>
            <Form onSubmit={handleLogin}>
                <div
                    className={styles.otp}
                    role="group"
                >
                    {
                        [0, 1, 2, 3, 4, 5].map(index => (
                            <Form.Input
                                key={index}
                                maxLength="1"
                                name={index}
                                ref={el => inputRefs.current[index] = el}
                                onChange={e => onChange(e, index)}
                                onFocus={onFocus}
                                onKeyDown={e => onKeyDown(e, index)}
                                onPaste={onPaste}
                                value={data[index]}
                                error={translate(fieldsErrors[index])}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                autoFocus={index === 0}
                            />
                        ))
                    }
                </div>
                <Errors
                    className={styles.error}
                    errors={errors}
                    translate={translate}
                />
                <div className={styles.receive}>
                    {translate('Check your spam or junk folder, or')}
                    <button
                        disabled={!email}
                        onClick={handleRequestPasswordOtp}
                    >
                        {translate('Resend code')}
                    </button>
                </div>
                <div id={idTurnstile} />
                <Form.Column mt>
                    <Form.Button disabled={isLoading}>
                        {translate('Verify and login')}
                    </Form.Button>
                </Form.Column>
            </Form>
            <button
                className={styles.back}
                onClick={handleBack}
            >
                {translate('Back to password login')}
            </button>
        </>
    )
};

export default OneTimePassword;
